import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, CheckCircle2, AlertCircle, ExternalLink, Copy } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { toast } from "sonner";

/**
 * Generic ChannelSetupWizard — drives off the per-channel field metadata
 * returned by GET /api/dropship/channels/catalog. One component handles
 * every connector (Shopify Custom App, WooCommerce, Amazon SP-API,
 * Walmart, TikTok Shop, Custom Webhook, Scheduled CSV) — no per-channel
 * React code needed. New connectors only require backend field metadata
 * to show up here.
 *
 * Flow:
 *   Step 1 · Channel picker — buyer chooses which channel.
 *   Step 2 · Credentials form — rendered from channel.fields[].
 *           "Test connection" hits POST /channels/{kind}/verify with
 *            the credential body; on success the buyer sees a green
 *            confirmation card before committing.
 *   Step 3 · Save & finish — hits POST /channels/{kind}/connect, which
 *            re-verifies + persists as a dropship_stores row. For
 *            custom_webhook we also surface the generated webhook URL
 *            + secret for the buyer to copy into their site.
 */
export default function ChannelSetupWizard({ open, onOpenChange, onConnected }) {
  const [step, setStep] = useState(1);
  const [catalog, setCatalog] = useState(null);   // /channels/catalog response
  const [chosen, setChosen] = useState(null);     // selected channel def
  const [form, setForm] = useState({});           // {field_name: value}
  const [verify, setVerify] = useState(null);     // verify response or {error}
  const [verifying, setVerifying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedResult, setSavedResult] = useState(null);  // {store, webhook_secret, inbound_webhook_url}

  // Reset internal state every time the dialog re-opens.
  useEffect(() => {
    if (!open) return;
    setStep(1); setChosen(null); setForm({}); setVerify(null); setSavedResult(null);
    api.get("/dropship/channels/catalog")
      .then((r) => setCatalog(r.data))
      .catch(() => toast.error("Could not load channel catalog"));
  }, [open]);

  const pickChannel = (ch) => {
    setChosen(ch);
    // Seed the form with default-empty strings for each field.
    const seed = {};
    (ch.fields || []).forEach((f) => {
      seed[f.name] = f.type === "select" && f.options?.length ? f.options[0].v : "";
    });
    setForm(seed);
    setVerify(null);
    setStep(2);
  };

  const updateField = (name, value) => {
    setForm((s) => ({ ...s, [name]: value }));
    setVerify(null);                              // any edit invalidates last verify
  };

  const requiredMissing = useMemo(() => {
    if (!chosen) return [];
    return (chosen.fields || [])
      .filter((f) => f.required !== false && !(form[f.name] || "").toString().trim())
      .map((f) => f.label);
  }, [chosen, form]);

  const handleTestConnection = async () => {
    if (!chosen) return;
    setVerifying(true); setVerify(null);
    try {
      const { data } = await api.post(
        `/dropship/channels/${chosen.kind}/verify`,
        form,
      );
      setVerify({ ok: true, ...data });
    } catch (e) {
      setVerify({ ok: false, error: e.response?.data?.detail || "Verification failed" });
    } finally { setVerifying(false); }
  };

  const handleConnect = async () => {
    if (!chosen) return;
    setSaving(true);
    try {
      const { data } = await api.post(
        `/dropship/channels/${chosen.kind}/connect`,
        form,
      );
      setSavedResult(data);
      setStep(3);
      toast.success(`Connected · ${chosen.label}`);
      onConnected && onConnected(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save connection");
    } finally { setSaving(false); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="display text-2xl tracking-tight">
            {step === 1 && "Connect a sales channel"}
            {step === 2 && chosen && `Set up ${chosen.label}`}
            {step === 3 && "Channel connected"}
          </DialogTitle>
          <div className="text-[12.5px] text-foreground/60 mt-1">
            {step === 1 && "Verqo is your source of truth — pick which channel you'll publish your mirrored catalog to."}
            {step === 2 && "Paste your credentials, test the connection, then save."}
            {step === 3 && savedResult?.info}
          </div>
        </DialogHeader>

        {/* progress dots */}
        <div className="flex gap-1.5 mt-2">
          {[1, 2, 3].map((n) => (
            <div key={n} className={`flex-1 h-1 rounded-full ${step >= n ? "bg-foreground" : "bg-secondary"}`}/>
          ))}
        </div>

        {/* ── Step 1 · channel grid ───────────────────────────────────── */}
        {step === 1 && (
          <div className="space-y-2 mt-3" data-testid="wizard-step-1">
            {!catalog && (
              <div className="text-sm text-foreground/55 py-12 text-center">Loading channels…</div>
            )}
            {catalog?.available?.map((ch) => (
              <button
                key={ch.kind}
                onClick={() => pickChannel(ch)}
                data-testid={`wizard-channel-${ch.kind}`}
                className="w-full text-left rounded-xl border border-border bg-white hover:border-foreground/30 hover:bg-secondary/30 transition-all p-3.5 flex items-start gap-3"
              >
                <div className="w-10 h-10 rounded-lg bg-secondary border border-border grid place-items-center text-[10px] mono uppercase shrink-0">
                  {ch.logo?.slice(0, 4) || "ch"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-[13.5px]">{ch.label}</div>
                    {ch.tier === "recommended" && (
                      <span className="text-[9.5px] mono uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">recommended</span>
                    )}
                    {ch.tier === "legacy" && (
                      <span className="text-[9.5px] mono uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-full bg-foreground/[0.06] text-foreground/55 border border-border">legacy</span>
                    )}
                    {ch.tier === "advanced" && (
                      <span className="text-[9.5px] mono uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">advanced</span>
                    )}
                  </div>
                  <div className="text-[11.5px] text-foreground/55 mt-0.5">{ch.tagline}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-foreground/40 mt-3 shrink-0"/>
              </button>
            ))}
            {catalog?.coming_soon?.length > 0 && (
              <div className="pt-3 border-t border-border" data-testid="wizard-coming-soon">
                <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55 mb-1.5">Coming soon</div>
                <div className="grid grid-cols-2 gap-1.5">
                  {catalog.coming_soon.map((ch) => (
                    <div key={ch.kind} className="rounded-lg border border-dashed border-border bg-secondary/20 px-2.5 py-2 text-[11px] text-foreground/55">
                      {ch.label}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Step 2 · credentials ───────────────────────────────────── */}
        {step === 2 && chosen && (
          <div className="space-y-3 mt-3" data-testid="wizard-step-2">
            {chosen.setup_hint && (
              <div className="rounded-lg border border-sky-200/70 bg-sky-50/40 px-3 py-2 text-[11.5px] text-sky-900">
                {chosen.setup_hint}
                {chosen.setup_url && (
                  <a href={chosen.setup_url} target="_blank" rel="noopener noreferrer"
                     className="ml-1 underline inline-flex items-center gap-0.5">
                    Setup guide <ExternalLink className="w-3 h-3"/>
                  </a>
                )}
              </div>
            )}

            {/* Render fields from metadata */}
            <div className="space-y-2.5">
              {(chosen.fields || []).map((f) => (
                <div key={f.name}>
                  <Label className="text-[12px]">
                    {f.label}
                    {f.required !== false && <span className="text-rose-500 ml-0.5">*</span>}
                  </Label>
                  {f.type === "select" ? (
                    <select
                      value={form[f.name] || ""}
                      onChange={(e) => updateField(f.name, e.target.value)}
                      data-testid={`wizard-field-${f.name}`}
                      className="w-full h-10 rounded-md border border-border bg-white px-3 text-[13px]"
                    >
                      {(f.options || []).map((o) => (
                        <option key={o.v} value={o.v}>{o.l}</option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      type={f.type === "password" ? "password" :
                              f.type === "url" ? "url" : "text"}
                      value={form[f.name] || ""}
                      onChange={(e) => updateField(f.name, e.target.value)}
                      placeholder={f.placeholder || ""}
                      data-testid={`wizard-field-${f.name}`}
                      className="h-10"
                    />
                  )}
                  {f.help && (
                    <div className="text-[10.5px] text-foreground/50 mt-1">{f.help}</div>
                  )}
                </div>
              ))}
              {chosen.fields?.length === 0 && (
                <div className="text-[12.5px] text-foreground/65 italic">
                  No credentials needed for this channel — we generate them on save.
                </div>
              )}
            </div>

            {/* Verify card — green/red feedback */}
            {chosen.kind !== "custom_webhook" && (chosen.fields || []).length > 0 && (
              <Button
                type="button"
                variant="outline"
                onClick={handleTestConnection}
                disabled={verifying || requiredMissing.length > 0}
                data-testid="wizard-test-connection"
                className="w-full h-9 rounded-full text-[12px]"
              >
                {verifying ? "Checking…" : "Test connection"}
              </Button>
            )}
            {verify?.ok && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-[12px] text-emerald-900 flex items-start gap-1.5" data-testid="wizard-verify-ok">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0"/>
                <div className="min-w-0">
                  <div className="font-medium">Connected</div>
                  {verify.meta && (
                    <div className="text-emerald-800/80 truncate">
                      {Object.entries(verify.meta).filter(([, v]) => v).slice(0, 3).map(([k, v]) =>
                        <span key={k} className="mr-2"><span className="opacity-60">{k}:</span> {String(v)}</span>
                      )}
                    </div>
                  )}
                  {verify.rate_limit_remaining != null && (
                    <div className="text-emerald-800/65 text-[11px]">{verify.rate_limit_remaining} API calls remaining</div>
                  )}
                </div>
              </div>
            )}
            {verify && !verify.ok && (
              <div className="rounded-lg border border-rose-200 bg-rose-50/60 px-3 py-2 text-[12px] text-rose-900 flex items-start gap-1.5" data-testid="wizard-verify-fail">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0"/>
                <div>{verify.error}</div>
              </div>
            )}

            <div className="flex gap-2 pt-2 border-t border-border">
              <Button variant="outline" onClick={() => setStep(1)} className="rounded-full h-10 px-4">
                <ArrowLeft className="w-4 h-4 mr-1"/>Back
              </Button>
              <Button
                onClick={handleConnect}
                disabled={saving || requiredMissing.length > 0 ||
                          (chosen.kind !== "custom_webhook" && (chosen.fields || []).length > 0 && !verify?.ok)}
                data-testid="wizard-save"
                className="flex-1 rounded-full h-10 bg-foreground text-background hover:bg-foreground/90"
              >
                {saving ? "Saving…" : "Connect channel"}
                <ArrowRight className="w-4 h-4 ml-1"/>
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 3 · success + custom-webhook secret reveal ─────────── */}
        {step === 3 && savedResult && (
          <div className="space-y-3 mt-3" data-testid="wizard-step-3">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 px-4 py-3 flex items-start gap-2.5">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0"/>
              <div>
                <div className="font-medium text-emerald-950">{chosen?.label} connected</div>
                <div className="text-[11.5px] text-emerald-900/75 mt-0.5">
                  We'll reconcile orders from this channel every 5 minutes.
                </div>
              </div>
            </div>

            {/* Custom webhook — reveal secret + URL only once. */}
            {savedResult.webhook_secret && (
              <div className="rounded-xl border border-amber-200 bg-amber-50/40 px-4 py-3 space-y-2.5" data-testid="wizard-webhook-creds">
                <div className="text-[12px] font-medium text-amber-950">
                  Copy these now — the secret is shown only once.
                </div>
                <div>
                  <Label className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55">Inbound webhook URL</Label>
                  <div className="flex gap-1.5 mt-0.5">
                    <Input readOnly value={savedResult.inbound_webhook_url || ""}
                           className="h-9 text-[11.5px] mono bg-white"/>
                    <Button size="sm" variant="outline" className="h-9 px-2.5"
                            onClick={() => copyToClipboard(savedResult.inbound_webhook_url || "")}>
                      <Copy className="w-3.5 h-3.5"/>
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55">Webhook secret (HMAC-SHA256)</Label>
                  <div className="flex gap-1.5 mt-0.5">
                    <Input readOnly value={savedResult.webhook_secret}
                           className="h-9 text-[11.5px] mono bg-white"/>
                    <Button size="sm" variant="outline" className="h-9 px-2.5"
                            onClick={() => copyToClipboard(savedResult.webhook_secret)}>
                      <Copy className="w-3.5 h-3.5"/>
                    </Button>
                  </div>
                </div>
                <div className="text-[11px] text-amber-900/75">
                  Each request must include <code className="bg-white/70 px-1 rounded">X-Verqo-Signature: sha256=&lt;hex digest&gt;</code> computed over the JSON body using this secret.
                </div>
              </div>
            )}

            <Button onClick={() => onOpenChange(false)} data-testid="wizard-finish"
                    className="w-full rounded-full h-11 bg-foreground text-background hover:bg-foreground/90">
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
